import type { Configuration, PopupRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

import Logger from '@/lib/logger';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: import.meta.env.VITE_AZURE_AUTHORITY,
    redirectUri: '/en/login',
    postLogoutRedirectUri: '/en/finish-logout', // Indicates the page to navigate after logout.
    /**
     * This option is not available on MSAL 5, so testing if this is required for our flow to work or it's the dependency version
     */
    navigateToLoginRequestUrl: false,
    // audience: import.meta.env.VITE_AZURE_AUDIENCE,
    knownAuthorities: [import.meta.env.VITE_AZURE_KNOWN_AUDIENCE],
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            Logger.error(message);
            return;
          case LogLevel.Info:
            Logger.info(message);
            return;
          case LogLevel.Verbose:
            Logger.debug(message);
            return;
          case LogLevel.Warning:
            Logger.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    'User.Read',
    // `${msalConfig.auth.clientId}/.default`,
    // 'api://aaf48483-d425-4651-8b01-ec6e0f60517e/login-and-api',
    // 'api://1e529d34-4101-45d7-bef9-f3054b63a0b3/api.access',
    // 'User.Read',
    // '[CLIENT_ID]/.default'
    // `[${msalConfig.auth.clientId}]/.default`,
    // 'https://graph.microsoft.com/.default',
    // 'api://b3a4f1d2-a8eb-4273-b679-3c478e3cd53c/User.Read',
  ],
};

export const changePasswordLoginRequest: PopupRequest = {
  scopes: ['User.Read'],
  prompt: 'login',
};

export const callApiRequest = {
  authority: `https://AtlasExternalID.ciamlogin.com/`,
  scopes: ['api://aaf48483-d425-4651-8b01-ec6e0f60517e/login-and-api'],
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me', //e.g. https://graph.microsoft.com/v1.0/me
};

export async function callMsGraph(accessToken: string) {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);

  const options = {
    method: 'GET',
    headers: headers,
  };

  return fetch(graphConfig.graphMeEndpoint, options)
    .then((response) => response.json())
    .catch((error) => Logger.log(error, 'callMsGraph'));
}
